import { API_ENDPOINTS, API_HEADERS } from "../constants/api";

export interface BlogApiPost {
  id: number;
  eng_title: string | null;
  eng_caption: string | null;
  eng_thumbnail: string | null;
  eng_video?: string | null;
  eng_content?: string | null;
  eng_description?: string | null;
  eng_tags?: { tag_name_eng?: string | null }[] | null;
  created_date?: string | null;
  /** First publish date; prefer over created_date for display. */
  published_at?: string | null;
}

export interface FetchBlogPostsParams {
  page?: number;
  pageSize?: number;
}

interface PaginatedBlogResponse {
  total?: number;
  results?: BlogApiPost[];
}

export interface BlogPostsResult {
  posts: BlogApiPost[];
  totalCount: number;
}

export const fetchBlogPostById = async (
  id: number | string
): Promise<BlogApiPost> => {
  const response = await fetch(API_ENDPOINTS.initiativeDetail(id), {
    headers: API_HEADERS,
  });

  if (!response.ok) {
    throw new Error(
      `Blog post detail request failed with status ${response.status}`
    );
  }

  const data: BlogApiPost = await response.json();
  return data;
};

const mapResponseToResult = (data: unknown): BlogPostsResult => {
  if (Array.isArray(data)) {
    return { posts: data, totalCount: data.length };
  }

  const paginated = data as PaginatedBlogResponse;
  if (Array.isArray(paginated?.results)) {
    return {
      posts: paginated.results,
      totalCount:
        typeof paginated.total === "number"
          ? paginated.total
          : paginated.results.length,
    };
  }

  return { posts: [], totalCount: 0 };
};

export const fetchBlogPosts = async (
  params: FetchBlogPostsParams = {}
): Promise<BlogPostsResult> => {
  const url = new URL(API_ENDPOINTS.posts);

  if (params.page) {
    url.searchParams.set("page", params.page.toString());
  }

  if (params.pageSize) {
    url.searchParams.set("pageSize", params.pageSize.toString());
  }

  const response = await fetch(url.toString(), {
    headers: API_HEADERS,
  });

  if (!response.ok) {
    throw new Error(`Blog posts request failed with status ${response.status}`);
  }

  const data: unknown = await response.json();
  return mapResponseToResult(data);
};
